import { motion } from "framer-motion";
import { Utensils, Factory, Building2, ShieldCheck, ArrowRight, CheckCircle2, Droplets, Award, TrendingUp } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export function Products() {
  const feedstocks = [
    {
      title: "Used Cooking Oil (UCO)",
      description: "Premium 2G feedstock from food businesses",
      image: "https://images.unsplash.com/photo-1610547939489-73202bc6afda?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb29raW5nJTIwb2lsJTIwZ29sZGVuJTIwbGlxdWlkfGVufDF8fHx8MTc2NTA5MDI5Nnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      stat: "Primary",
      gradient: "from-[#7CB342] to-[#689F38]",
    },
    {
      title: "Trap Grease Oil (FOG)",
      description: "High-potential feedstock from commercial facilities",
      image: "https://images.unsplash.com/photo-1729909019771-7728f56647f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1c2VkJTIwY29va2luZyUyMG9pbCUyMHJlc3RhdXJhbnR8ZW58MXx8fHwxNzY1MDg5NzQxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      stat: "2G & 3G",
      gradient: "from-[#1976D2] to-[#1565C0]",
    },
    {
      title: "Industrial Waste Oils",
      description: "Spent earth bleach and refinery waste",
      image: "https://images.unsplash.com/photo-1619070284836-e850273d69ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmYWN0b3J5JTIwaW5kdXN0cmlhbHxlbnwxfHx8fDE3NjUwOTA5NzZ8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      stat: "Industrial",
      gradient: "from-green-500 to-emerald-600",
    },
  ];

  const partners = [
    {
      icon: Utensils,
      title: "Food Business Operators",
      subtitle: "Restaurants • Hotels • Cloud Kitchens",
      image: "https://images.unsplash.com/photo-1760888549060-11fdfe935840?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwb3duZXIlMjBjaGVmfGVufDF8fHx8MTc2NTA1NjM3Mnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      gradient: "from-[#7CB342] to-[#689F38]",
      solutions: [
        "Free UCO collection with instant payment",
        "Storage drums & collection infrastructure",
        "Digital documentation & compliance",
        "Zero disposal costs, generate revenue",
      ],
      benefits: [
        { icon: CheckCircle2, text: "Instant Payment" },
        { icon: CheckCircle2, text: "Free Equipment" },
        { icon: CheckCircle2, text: "Legal Compliance" },
      ],
    },
    {
      icon: Factory,
      title: "Biofuel Manufacturers",
      subtitle: "Biodiesel • SAF • Renewable Diesel",
      image: "https://images.unsplash.com/photo-1761195696590-3490ea770aa1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmYWN0b3J5JTIwbWFuYWdlciUyMGluZHVzdHJpYWx8ZW58MXx8fHwxNzY1MDkxMzgyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      gradient: "from-[#1976D2] to-[#1565C0]",
      solutions: [
        "ISCC-certified feedstock supply",
        "100% digitally traceable batches",
        "Consistent quality & specifications",
        "Export-ready documentation",
      ],
      benefits: [
        { icon: Award, text: "ISCC Certified" },
        { icon: Droplets, text: "Quality Assured" },
        { icon: TrendingUp, text: "Scalable Supply" },
      ],
    },
    {
      icon: Building2,
      title: "Policy & Regulators",
      subtitle: "Compliance • Monitoring • Reporting",
      image: "https://images.unsplash.com/photo-1640200330428-9fe2ab926de1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxnb3Zlcm5tZW50JTIwb2ZmaWNpYWwlMjBtZWV0aW5nfGVufDF8fHx8MTc2NTA5MTM4Mnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      gradient: "from-green-500 to-emerald-600",
      solutions: [
        "Real-time tracking & monitoring",
        "Complete audit trail & transparency",
        "Policy compliance reporting",
        "Environmental impact metrics",
      ],
      benefits: [
        { icon: ShieldCheck, text: "Full Transparency" },
        { icon: CheckCircle2, text: "Audit Ready" },
        { icon: TrendingUp, text: "Impact Metrics" },
      ],
    },
  ];

  return (
    <section id="products" className="relative py-20 lg:py-32 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#7CB342]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#1976D2]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border-2 border-[#7CB342]/30 rounded-full px-5 py-2.5 mb-6"
          >
            <Droplets className="w-5 h-5 text-[#7CB342]" />
            <span className="text-sm font-bold text-gray-900">Our Products & Solutions</span>
          </motion.div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Premium{" "}
            <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
              Renewable Feedstocks
            </span>
          </h2>

          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            ISCC-certified, 100% traceable renewable fuel feedstock for the global clean energy market
          </p>
        </motion.div>

        {/* Feedstock Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {feedstocks.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              {/* Glow */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${item.gradient} rounded-2xl opacity-0 group-hover:opacity-50 blur transition-all`} />
              
              {/* Card */}
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all h-full">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <ImageWithFallback
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${item.gradient} opacity-60`} />
                  
                  {/* Badge */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="text-sm font-black text-gray-900">{item.stat}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-black text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Partner Solutions Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
            Tailored Solutions for{" "}
            <span className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
              Every Partner
            </span>
          </h3>
          <p className="text-lg text-gray-600">
            Where Waste Becomes Power - Customized solutions for your needs
          </p>
        </motion.div>

        {/* Partner Solution Cards - Creative Layout */}
        <div className="space-y-12">
          {partners.map((partner, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`relative ${index % 2 === 0 ? '' : 'lg:ml-auto'}`}
            >
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Content Side */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative">
                    {/* Background Decoration */}
                    <div className={`absolute -inset-4 bg-gradient-to-r ${partner.gradient} opacity-10 rounded-3xl blur-2xl`} />
                    
                    <div className="relative bg-white border-2 border-gray-200 rounded-3xl p-8 lg:p-10 shadow-xl hover:border-transparent hover:shadow-2xl transition-all group">
                      {/* Icon */}
                      <div className={`inline-flex bg-gradient-to-br ${partner.gradient} rounded-2xl p-4 mb-6 group-hover:scale-110 transition-transform`}>
                        <partner.icon className="w-8 h-8 text-white" />
                      </div>

                      {/* Title */}
                      <h4 className="text-2xl lg:text-3xl font-black text-gray-900 mb-2">
                        {partner.title}
                      </h4>
                      <p className="text-gray-600 font-semibold mb-6">{partner.subtitle}</p>

                      {/* Solutions List */}
                      <div className="space-y-3 mb-6">
                        {partner.solutions.map((solution, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className={`bg-gradient-to-br ${partner.gradient} rounded-lg p-1 mt-0.5`}>
                              <ArrowRight className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium flex-1">{solution}</span>
                          </div>
                        ))}
                      </div>

                      {/* Benefits */}
                      <div className="flex flex-wrap gap-3 pt-6 border-t border-gray-200">
                        {partner.benefits.map((benefit, idx) => (
                          <div key={idx} className={`inline-flex items-center gap-2 bg-gradient-to-r ${partner.gradient} px-4 py-2 rounded-full`}>
                            <benefit.icon className="w-4 h-4 text-white" />
                            <span className="text-sm font-bold text-white">{benefit.text}</span>
                          </div>
                        ))}
                      </div>

                      {/* CTA */}
                      <button className={`mt-6 w-full bg-gradient-to-r ${partner.gradient} text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 group/btn`}>
                        Partner With Us
                        <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Image Side */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="relative"
                  >
                    <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                      <ImageWithFallback
                        src={partner.image}
                        alt={partner.title}
                        className="w-full h-[400px] lg:h-[500px] object-cover"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${partner.gradient} opacity-30`} />
                    </div>
                    
                    {/* Decorative Element */}
                    <div className={`absolute ${index % 2 === 0 ? '-right-6 -bottom-6' : '-left-6 -bottom-6'} w-32 h-32 bg-gradient-to-br ${partner.gradient} opacity-20 rounded-full blur-2xl`} />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="relative inline-block">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#7CB342] to-[#1976D2] opacity-20 rounded-3xl blur-2xl" />
            <div className="relative bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-3xl p-12 lg:p-16 text-white">
              <h3 className="text-3xl lg:text-4xl font-black mb-4">
                Ready to Transform Waste Into Power?
              </h3>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                Join KRB Clean Energy Private Limited and be part of India's clean energy revolution
              </p>
              <button className="bg-white text-gray-900 px-10 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105">
                Get Started Today
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}